import { signedDistanceInDirectionOf } from '@geometry'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

// Safety tolerance for split point validation (distance from opening start/end)
export const SPLIT_SAFETY_TOLERANCE = 0.01

/** Data for creating an additional rip on a new wall segment (relative_position only; wall_guid and guid set when creating) */
export interface AdditionalRipToCreate {
  relative_position: string
}

export interface SplitWallData {
  newWall1: {
    p1: { x: number; y: number }
    p2: { x: number; y: number }
    storey_idx: number
    placement: WallPlacement
    openings: Opening[]
    additionalRips: AdditionalRipToCreate[]
  }
  newWall2: {
    p1: { x: number; y: number }
    p2: { x: number; y: number }
    storey_idx: number
    placement: WallPlacement
    openings: Opening[]
    additionalRips: AdditionalRipToCreate[]
  }
}

/**
 * Calculates the position of an opening along the wall axis
 * @param wallStart - Start point of the wall
 * @param wallAxisDirection - Normalized direction vector of the wall axis
 * @param openingPoint - A point from the opening (start or end)
 * @returns Distance along the wall axis from wallStart to the projected opening point
 */
const getOpeningPositionAlongAxis = (
  wallStart: ImmutableVector3,
  wallAxisDirection: ImmutableVector3,
  openingPoint: Point,
): number => {
  const openingVec = new ImmutableVector3(openingPoint.x, openingPoint.y, openingPoint.z)
  return signedDistanceInDirectionOf(wallStart, openingVec, wallAxisDirection)
}

/**
 * Creates a copy of an opening (openings are kept in global coordinates on the wall plane)
 * @param opening - The original opening
 * @returns Copy of the opening
 */
const copyOpening = (opening: Opening): Opening => {
  // Create a deep copy of the opening, preserving all point properties
  return JSON.parse(JSON.stringify(opening))
}

/**
 * Projects a rip's start point onto the wall axis and returns the signed distance from wall start.
 * Same approach as in WallRipForm handleReplace: rip point is projected along the wall axis.
 */
const getRipPositionAlongAxis = (
  wallStart: ImmutableVector3,
  wallAxisDirection: ImmutableVector3,
  ripStart: Point,
): number => {
  const ripStartVec = new ImmutableVector3(ripStart.x, ripStart.y, ripStart.z)
  return signedDistanceInDirectionOf(wallStart, ripStartVec, wallAxisDirection)
}

export const splitWall = (wall: ShapeObject, splitPoint: Point, rips?: Rip[]): SplitWallData => {
  const {
    shape: { points },
  } = wall

  const p0 = new ImmutableVector3(points[0].x, points[0].y, points[0].z)
  const p1 = new ImmutableVector3(points[1].x, points[1].y, points[1].z)
  const split = new ImmutableVector3(splitPoint.x, splitPoint.y, splitPoint.z)

  // Calculate wall axis direction (from point 0 to point 1)
  const wallAxisDirection = p1.sub(p0).normalize()
  const wallLength = p0.distanceTo(p1)

  // Project split point onto wall axis
  const distanceP0ToSplitPointAlongAxis = signedDistanceInDirectionOf(p0, split, wallAxisDirection)
  const segment1Length = distanceP0ToSplitPointAlongAxis
  const segment2Length = wallLength - distanceP0ToSplitPointAlongAxis

  // Calculate the projected split point on the wall axis
  const projectedSplitPoint = p0.add(
    wallAxisDirection.multiplyScalar(distanceP0ToSplitPointAlongAxis),
  )

  // Process openings: determine which belong to which wall segment
  const openings1: Opening[] = []
  const openings2: Opening[] = []

  if (wall.openings) {
    const nonLocalOpenings = wall.openings.filter(opening => !opening.is_local)

    for (const opening of nonLocalOpenings) {
      const openingStart = opening.shape.points[0]
      const openingEnd = opening.shape.points[1]

      // Calculate opening positions along the wall axis
      const openingStartPos = getOpeningPositionAlongAxis(p0, wallAxisDirection, openingStart)
      const openingEndPos = getOpeningPositionAlongAxis(p0, wallAxisDirection, openingEnd)

      // Determine which wall segment the opening belongs to
      // Opening belongs to wall1 if it's entirely before the split point
      // Opening belongs to wall2 if it's entirely after the split point
      const openingMinPos = Math.min(openingStartPos, openingEndPos)
      const openingMaxPos = Math.max(openingStartPos, openingEndPos)

      if (openingMaxPos <= distanceP0ToSplitPointAlongAxis) {
        // Opening is entirely in wall1 - keep opening as-is (already relative to p0)
        openings1.push(copyOpening(opening))
      } else if (openingMinPos >= distanceP0ToSplitPointAlongAxis) {
        // Opening is entirely in wall2 - keep opening as-is (openings are in global coordinates)
        openings2.push(copyOpening(opening))
      }
      // If opening spans the split point, it's not added to either wall (should be prevented by validation)
    }
  }

  // Process additional rips: filter by wall and RipType, then distribute by position along wall axis
  const additionalRips1: AdditionalRipToCreate[] = []
  const additionalRips2: AdditionalRipToCreate[] = []

  if (rips && wall.guid) {
    const additionalRipsOnWall = rips.filter(
      (rip): rip is Rip => rip.wall_guid === wall.guid && rip.rip_type === 'AdditionalRip',
    )

    for (const rip of additionalRipsOnWall) {
      const positionAlongAxis = getRipPositionAlongAxis(p0, wallAxisDirection, rip.start)

      if (segment1Length > 0 && positionAlongAxis <= distanceP0ToSplitPointAlongAxis) {
        const relativePosition = positionAlongAxis <= 0 ? 0 : positionAlongAxis / segment1Length
        additionalRips1.push({
          relative_position: String(Math.min(1, Math.max(0, relativePosition))),
        })
      } else if (segment2Length > 0 && positionAlongAxis >= distanceP0ToSplitPointAlongAxis) {
        const offsetInSegment2 = positionAlongAxis - distanceP0ToSplitPointAlongAxis
        const relativePosition =
          offsetInSegment2 >= segment2Length ? 1 : offsetInSegment2 / segment2Length
        additionalRips2.push({
          relative_position: String(Math.min(1, Math.max(0, relativePosition))),
        })
      }
    }
  }

  // Create updated wall (from p0 to projected split point)
  const newWall1 = {
    p1: {
      x: p0.x,
      y: p0.y,
    },
    p2: {
      x: projectedSplitPoint.x,
      y: projectedSplitPoint.y,
    },
    storey_idx: Number(wall.storey),
    placement: (wall.placement || 'Internal') as WallPlacement,
    openings: openings1,
    additionalRips: additionalRips1,
  }

  // Create new wall (from projected split point to p1)
  const newWall2 = {
    p1: {
      x: projectedSplitPoint.x,
      y: projectedSplitPoint.y,
    },
    p2: {
      x: p1.x,
      y: p1.y,
    },
    storey_idx: Number(wall.storey),
    placement: (wall.placement || 'Internal') as WallPlacement,
    openings: openings2,
    additionalRips: additionalRips2,
  }

  return {
    newWall1,
    newWall2,
  }
}
